/**
 * Stap 1: maak je groepje -- wie doen er mee, hoe heet het, welk icoon.
 *
 * Het groepje (naam, icoon, genoten) staat in het scherm (LeerlingFlow),
 * want de chat en het argumentenformulier tonen het ook, en wie teruggaat
 * naar het welkomscherm moet het niet opnieuw hoeven invullen. Het
 * formulier voor groepsgenoten staat in onderdelen/Groepsgenoten.js.
 */
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { kleur } from '../../../theme/tokens';
import { basis } from '../stijlen';
import { Groepsgenoten, formulierStijlen } from '../onderdelen/Groepsgenoten';

const AVATARS = ['🦊', '🐻', '🦁', '🐼', '🐨', '🐸', '🦉', '🐙', '🦋', '🐝', '🐶', '🐱', '🐰', '🦄', '🐢', '🦈', '🦅', '🦆', '🐧'];

const AVATAR_NAMEN = {
  '🦊': 'De Vossen', '🐻': 'De Beren', '🦁': 'De Leeuwen', '🐼': 'De Panda\'s',
  '🐨': 'De Koala\'s', '🐸': 'De Kikkers', '🦉': 'De Uilen', '🐙': 'De Octopussen',
  '🦋': 'De Vlinders', '🐝': 'De Bijen', '🐶': 'De Honden', '🐱': 'De Katten',
  '🐰': 'De Konijnen', '🦄': 'De Eenhoorns', '🐢': 'De Schildpadden', '🦈': 'De Haaien',
  '🦅': 'De Adelaars', '🦆': 'De Eenden', '🐧': 'De Pinguïns',
};

export function Groepje({ api, session, user, groepje, onWijzigGroepje, onTerug, onKlaar }) {
  // Melding onder het groepsnaamveld, in plaats van een alert-venster: een
  // kind dat een naam moet aanpassen leest liever een regel op de plek waar
  // hij typt dan een pop-up die hij moet wegklikken.
  const [fout, setFout] = useState('');

  const kiesAvatar = (avatar) => {
    // De naam verandert mee met het icoon.
    onWijzigGroepje({ ...groepje, avatar, naam: AVATAR_NAMEN[avatar] || groepje.naam });
  };

  const maakGroepje = async () => {
    setFout('');
    if (!groepje.naam.trim()) { setFout('Vul eerst een groepsnaam in.'); return; }
    try {
      const data = await api.maakGroepje(session.session_id, groepje.naam, groepje.avatar, groepje.genoten.map(g => g.bewijs));
      if (data.success) onKlaar(data.data);
      else setFout(data.detail || 'Dat lukte niet. Probeer het nog eens.');
    } catch (err) {
      setFout('Geen verbinding. Probeer het nog eens.');
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.stepHeader}>
        <span style={styles.stepNumber}>1</span>
        <div><h2 style={styles.stepTitle}>Maak je groepje</h2><p style={styles.stepSubtitle}>Wie doen er mee, en hoe heet jullie groepje?</p></div>
      </div>

      <Groepsgenoten api={api} sessionId={session.session_id} user={user} genoten={groepje.genoten}
        onWijzig={(wijzig) => onWijzigGroepje(g => ({ ...g, genoten: wijzig(g.genoten) }))} />

      <div style={styles.formGroup}>
        <label style={styles.label}>Groepsnaam</label>
        <input type="text" value={groepje.naam}
          onChange={(e) => { onWijzigGroepje({ ...groepje, naam: e.target.value }); setFout(''); }}
          placeholder="Bijv. De Denkers" style={styles.input} maxLength={30} />
        {fout && <p style={styles.genootFout}>{fout}</p>}
      </div>
      <div style={styles.formGroup}>
        <label style={styles.label}>Kies een avatar</label>
        <div style={styles.avatarGrid}>
          {AVATARS.map(avatar => (
            <button key={avatar} onClick={() => kiesAvatar(avatar)}
              style={groepje.avatar === avatar ? styles.avatarBtnActive : styles.avatarBtn}>{avatar}</button>
          ))}
        </div>
      </div>
      <div style={styles.preview}>
        <span style={styles.previewAvatar}>{groepje.avatar}</span>
        <span style={styles.previewName}>{groepje.naam || 'Jouw groepsnaam'}</span>
      </div>
      <div style={styles.buttonRow}>
        <button onClick={onTerug} style={styles.btnSecondary}><ChevronLeft size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Terug</button>
        <button onClick={maakGroepje} style={styles.btn}>Verder<ChevronRight size={16} style={{ marginLeft: '4px', verticalAlign: 'middle' }} /></button>
      </div>
    </div>
  );
}

const styles = {
  ...basis,
  ...formulierStijlen,
  avatarGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' },
  avatarBtn: { padding: '12px', fontSize: '28px', background: kleur.vlakGrijs, border: '2px solid transparent', borderRadius: '12px', cursor: 'pointer' },
  avatarBtnActive: { padding: '12px', fontSize: '28px', background: kleur.accentLicht, border: `2px solid ${kleur.accent}`, borderRadius: '12px', cursor: 'pointer' },
  preview: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: kleur.vlak, borderRadius: '12px', marginBottom: '24px' },
  previewAvatar: { fontSize: '32px' },
  previewName: { fontSize: '18px', fontWeight: 'bold', color: kleur.inkt },
};
